import React, { useState } from 'react';
import { Button, Dialog, DialogTitle } from 'components/Elements';
import { X, Trash2 } from 'lucide-react';
import { useNotificationStore } from 'stores/notifications';
import { useUserSessions } from '../api/getUserSessions';
import { useRevokeUserSession } from '../api/revokeUserSession';
import { useRevokeAllSessions } from '../api/revokeAllSessions';
import { formatDate } from 'utils/format';

export const Sessions = ({ userId }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { data: sessions, isLoading, refetch } = useUserSessions({ userId });

  const { mutate: revokeSession, isLoading: isRevokingOne } =
    useRevokeUserSession({
      onSuccess: () => {
        refetch();
        addNotification({ type: 'success', title: 'Session revoked' });
      },
    });

  const { mutate: revokeAll, isLoading: isRevokingAll } = useRevokeAllSessions({
    onSuccess: () => {
      refetch();
      addNotification({ type: 'success', title: 'All sessions revoked' });
    },
  });

  const { addNotification } = useNotificationStore();

  const handleOpen = () => setIsDialogOpen(true);
  const handleClose = () => setIsDialogOpen(false);

  return (
    <>
      <button
        onClick={handleOpen}
        type="button"
        className="border border-white/10 p-1 px-2 rounded-lg hover:bg-white/10 text-xs"
      >
        Sessions
      </button>

      <Dialog isOpen={isDialogOpen} onClose={handleClose}>
        <div className="relative bg-[linear-gradient(to_bottom,_#2F6ACB,_#4455D0,_#4B3CBF)] dark:bg-[linear-gradient(to_bottom,_#0F2027,_#203A43,_#2C5364)] shadow-2xl rounded-2xl px-6 pt-5 pb-6 inline-block align-bottom text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="flex items-center justify-between mb-4">
            <DialogTitle className="text-xl font-bold text-white">
              Active Sessions
            </DialogTitle>
            <button
              onClick={handleClose}
              className="text-white/60 hover:text-white/90 transition-colors"
            >
              <X />
            </button>
          </div>

          <div className="space-y-3 max-h-96 overflow-y-auto">
            {isLoading ? (
              Array.from({ length: 3 }).map((_, idx) => (
                <div
                  key={idx}
                  className="animate-pulse bg-white/10 h-16 w-full rounded-md"
                />
              ))
            ) : sessions?.sessions?.length ? (
              sessions.sessions.map((session) => {
                const {
                  id,
                  uniqueId,
                  expiresAt,
                  biometrics,
                  currentSession,
                  deviceInfo,
                  deviceType,
                  accountType,
                } = session;

                return (
                  <div
                    key={id}
                    className="bg-white/10 p-3 rounded-md text-sm text-white/90 flex justify-between items-start"
                  >
                    <div>
                      <p className="font-semibold capitalize">
                        {deviceType || 'Unknown Device'}
                        {currentSession && (
                          <span className="ml-2 text-xs text-green-400">
                            (This device)
                          </span>
                        )}
                      </p>

                      <p className="text-white/60 text-xs">
                        Account Type: {accountType || 'N/A'} · Auth:{' '}
                        {biometrics || 'N/A'}
                      </p>

                      <p className="text-white/60 text-xs">
                        Device ID: {uniqueId}
                      </p>

                      {deviceInfo && (
                        <p className="text-white/60 text-xs truncate">
                          Device Info: {Object.values(deviceInfo).join(', ')}
                        </p>
                      )}

                      <p className="text-white/60 text-xs">
                        Expires: {formatDate(expiresAt)}
                      </p>
                    </div>

                    {!currentSession && (
                      <button
                        disabled={isRevokingOne}
                        onClick={() => revokeSession({ data: { id } })}
                        className="text-red-400 hover:text-red-600 transition-colors text-xs"
                        title="Revoke Session"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })
            ) : (
              <p className="text-white/70 text-sm">No active sessions.</p>
            )}
          </div>

          {sessions?.sessions?.length > 1 && (
            <Button
              onClick={() => revokeAll({ userId })}
              isLoading={isRevokingAll}
              variant="darkGlass"
              className="w-full mt-4"
            >
              {isRevokingAll ? 'Revoking...' : 'Revoke All Sessions'}
            </Button>
          )}
        </div>
      </Dialog>
    </>
  );
};
